import moment from "moment";

function hireMoment(e) {
  const raw = e?.workAnniversaryDate;
  if (!raw) return null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const m = moment(trimmed.slice(0, 10), "YYYY-MM-DD", true).startOf("day");
      if (m.isValid()) return m;
    }
  }
  const m = moment(raw).startOf("day");
  return m.isValid() ? m : null;
}

function displayName(e) {
  const n = `${e?.firstname || ""} ${e?.lastname || ""}`.trim();
  return n || e?.email || "Team member";
}

/**
 * Upcoming birthdays within the next `windowDays` (inclusive), based on calendar month/day.
 */
export function getUpcomingBirthdays(employees, windowDays = 14) {
  const from = moment().startOf("day");
  const until = from.clone().add(windowDays, "days").endOf("day");
  const out = [];

  for (const e of employees || []) {
    if (!e?.dob) continue;
    const birth = moment(e.dob);
    if (!birth.isValid()) continue;

    let next = from.clone().month(birth.month()).date(birth.date()).startOf("day");
    if (next.isBefore(from, "day")) {
      next = next.add(1, "year");
    }
    if (next.isSameOrAfter(from, "day") && next.isSameOrBefore(until, "day")) {
      out.push({
        userId: e.userId,
        name: displayName(e),
        profileImage: e.profileImage,
        nextDate: next,
      });
    }
  }

  return out.sort((a, b) => a.nextDate.valueOf() - b.nextDate.valueOf());
}

/**
 * Birthdays in the current calendar month (includes past and upcoming dates in the month).
 */
export function getBirthdaysThisMonth(employees, ref = moment()) {
  const today = ref.clone().startOf("day");
  const monthIndex = ref.month();
  const out = [];

  for (const e of employees || []) {
    if (!e?.dob) continue;
    const birth = moment(e.dob);
    if (!birth.isValid() || birth.month() !== monthIndex) continue;

    const occurrence = ref.clone().date(birth.date()).startOf("day");
    if (!occurrence.isValid() || occurrence.month() !== monthIndex) continue;

    out.push({
      userId: e.userId,
      name: displayName(e),
      profileImage: e.profileImage,
      nextDate: occurrence,
      isPast: occurrence.isBefore(today, "day"),
    });
  }

  return out.sort((a, b) => a.nextDate.valueOf() - b.nextDate.valueOf());
}

/** Birthdays happening today (calendar month/day). */
export function getBirthdaysToday(employees) {
  const today = moment().startOf("day");
  const out = [];
  for (const e of employees || []) {
    if (!e?.dob) continue;
    const birth = moment(e.dob);
    if (!birth.isValid()) continue;
    if (today.month() === birth.month() && today.date() === birth.date()) {
      out.push({
        userId: e.userId,
        name: displayName(e),
        profileImage: e.profileImage,
      });
    }
  }
  return out;
}

/**
 * Work anniversaries today — only when admin has set `workAnniversaryDate`.
 */
export function getWorkAnniversariesToday(employees) {
  const today = moment().startOf("day");
  const out = [];
  for (const e of employees || []) {
    const hired = hireMoment(e);
    if (!hired) continue;
    if (today.month() !== hired.month() || today.date() !== hired.date()) continue;
    const years = today.diff(hired, "years");
    if (years < 1) continue;

    out.push({
      userId: e.userId,
      name: displayName(e),
      profileImage: e.profileImage,
      years,
    });
  }
  return out;
}

/**
 * Work anniversaries (hire date) within the next `windowDays`.
 */
export function getUpcomingAnniversaries(employees, windowDays = 14) {
  const from = moment().startOf("day");
  const until = from.clone().add(windowDays, "days").endOf("day");
  const out = [];

  for (const e of employees || []) {
    const joined = hireMoment(e);
    if (!joined) continue;

    let next = from.clone().month(joined.month()).date(joined.date()).startOf("day");
    if (next.isBefore(from, "day")) {
      next = next.add(1, "year");
    }
    if (next.isSameOrAfter(from, "day") && next.isSameOrBefore(until, "day")) {
      const years = next.diff(joined, "years");
      if (years < 1) continue;

      out.push({
        userId: e.userId,
        name: displayName(e),
        profileImage: e.profileImage,
        nextDate: next,
        yearsOfService: years,
      });
    }
  }

  return out.sort((a, b) => a.nextDate.valueOf() - b.nextDate.valueOf());
}

/**
 * Work anniversaries in the current calendar month (includes past and upcoming dates in the month).
 */
export function getAnniversariesThisMonth(employees, ref = moment()) {
  const today = ref.clone().startOf("day");
  const monthIndex = ref.month();
  const out = [];

  for (const e of employees || []) {
    const joined = hireMoment(e);
    if (!joined || joined.month() !== monthIndex) continue;

    const occurrence = ref.clone().date(joined.date()).startOf("day");
    if (!occurrence.isValid() || occurrence.month() !== monthIndex) continue;

    const years = occurrence.diff(joined, "years");
    if (years < 1) continue;

    out.push({
      userId: e.userId,
      name: displayName(e),
      profileImage: e.profileImage,
      nextDate: occurrence,
      yearsOfService: years,
      isPast: occurrence.isBefore(today, "day"),
    });
  }

  return out.sort((a, b) => a.nextDate.valueOf() - b.nextDate.valueOf());
}
